import './ChangeRecordsFilter.scss';
import { ChangeCountsRow } from './ChangeCountsRow';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { DateRangePickerParams } from '@ui/DateRangePicker';
import { getTimelineSummarySelector } from '@timeline/@arch/selectors';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';
import { targetObjectTypeOrder } from '@sidebar/SearchItems';
import { TimelineObjectFilter } from '@timeline/@arch/lib';
import { Button, Divider } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { TimelineChangeCounts, TimelineChangeType } from '@sonar-software/sonar-core';
import { UpdateQueryParams, useSonarSelector } from '@hooks';

type DateRangeRequestParams = DateRangePickerParams & { tzoffset: number };

export type ChangeRecordsFilterParams = DateRangeRequestParams & { objectFilter: TimelineObjectFilter };

export type ObjectTypeChangeCounts = TimelineChangeCounts & {
	sonarObjectType: SonarObjectType;
};

const sonarObjectTypeOrder = [ ...targetObjectTypeOrder, SonarObjectType.SalesforceProfile ];

const defaultChangeCounts: TimelineChangeCounts = {
	discovered: 0,
	modified: 0,
	vanished: 0
};

const addFilter = (
	objectFilter: TimelineObjectFilter,
	objectType: SonarObjectType,
	changeType: TimelineChangeType
) => {
	objectFilter[ objectType ] = {
		...objectFilter[ objectType ],
		[ changeType ]: true
	};
};

const removeFilter = (
	objectFilter: TimelineObjectFilter,
	objectType: SonarObjectType,
	changeType: TimelineChangeType
) => {
	delete objectFilter[ objectType ]?.[ changeType ];

	if (!Object.keys(objectFilter[ objectType ] ?? {}).length) {
		delete objectFilter[ objectType ];
	}
};

export interface ChangeRecordsFilterProps {
	clearSelectedChangeType: () => void;
	highlightedChangeType?: TimelineChangeType;
	onChange: UpdateQueryParams<ChangeRecordsFilterParams>;
	params: ChangeRecordsFilterParams;
	selectedChangeType?: TimelineChangeType;
}

export const ChangeRecordsFilter: React.FC<ChangeRecordsFilterProps> = ({
	clearSelectedChangeType,
	highlightedChangeType,
	onChange,
	params,
	selectedChangeType
}) => {
	const summary = useSonarSelector(getTimelineSummarySelector.data);

	const changeCounts: ObjectTypeChangeCounts[] = useMemo(
		() => sonarObjectTypeOrder.filter(
			(sonarObjectType) => summary?.changeCounts[ sonarObjectType ] || params.objectFilter?.[ sonarObjectType ]
		).map((sonarObjectType) => ({
			...summary?.changeCounts[ sonarObjectType ] ?? defaultChangeCounts,
			sonarObjectType
		})),
		[ params.objectFilter, summary?.changeCounts ]
	);

	useEffect(() => {
		if (selectedChangeType) {
			const objectFilter = { ...params.objectFilter };

			const allSelected = Object.keys(summary?.changeCounts ?? []).every((objectType: SonarObjectType) =>
				objectFilter[ objectType ]?.[ selectedChangeType ]);

			if (allSelected) {
				Object.keys(summary?.changeCounts ?? []).forEach((objectType: SonarObjectType) => {
					removeFilter(objectFilter, objectType, selectedChangeType);
				});
			} else {
				Object.keys(summary?.changeCounts ?? []).forEach((objectType: SonarObjectType) => {
					addFilter(objectFilter, objectType, selectedChangeType);
				});
			}

			onChange({ objectFilter });
			clearSelectedChangeType();
		}
	}, [ clearSelectedChangeType, onChange, params.objectFilter, selectedChangeType, summary?.changeCounts ]);

	const onClick = useCallback((changeType: TimelineChangeType, sonarObjectType: SonarObjectType) => {
		const objectFilter = { ...params?.objectFilter };

		if (objectFilter[ sonarObjectType ]?.[ changeType ]) {
			removeFilter(objectFilter, sonarObjectType, changeType);
		} else {
			addFilter(objectFilter, sonarObjectType, changeType);
		}

		onChange({ objectFilter });
	}, [ onChange, params?.objectFilter ]);

	const onClickObjectType = useCallback((sonarObjectType: SonarObjectType) => {
		const objectFilter = { ...params?.objectFilter };

		if (Object.keys(objectFilter[ sonarObjectType ] ?? {}).length === 3) {
			delete objectFilter[ sonarObjectType ];
		} else {
			objectFilter[ sonarObjectType ] = {
				[ TimelineChangeType.Discovered ]: true,
				[ TimelineChangeType.Modified ]: true,
				[ TimelineChangeType.Vanished ]: true
			};
		}

		onChange({ objectFilter });
	}, [ onChange, params?.objectFilter ]);

	const clearFilters = useCallback(() => {
		onChange({ objectFilter: {} });
	}, [ onChange ]);

	if (!changeCounts.length) {
		return null;
	}

	const hasFilters = Object.keys(params?.objectFilter ?? {}).length;

	return (
		<div className='change-records-filter'>
			<Divider />
			{
				changeCounts?.map(
					(counts) => <ChangeCountsRow
						counts={ counts }
						highlightedChangeType={ highlightedChangeType }
						key={ counts.sonarObjectType }
						onClick={ onClick }
						onClickObjectType={ onClickObjectType }
						params={ params } />
				)
			}
			<Button
				disabled={ !hasFilters }
				onClick={ clearFilters }>
				{ COPY_SPECIFIC.CLEAR_ENTITY_FILTERS }
			</Button>
		</div>
	);
};
